import { useEffect, useState } from 'react';
import type { DistributionClient } from '../lib/api';
import type { CatalogFacets, CatalogFilterSelection } from '../lib/types';
import { useI18n } from '../lib/use-i18n';
import {
  EMPTY_CATALOG_FILTER,
  catalogFilterActive,
  providerChipSelected,
  toggleCatalogId as toggle,
  toggleProviderChip,
} from '../lib/catalog-filter';

/** Genre and streaming-service chips with the library's counts for the current selection. */
export function CatalogFilters({ client, kind, selection, onChange }: {
  client: DistributionClient | null;
  kind: 'movie' | 'series';
  selection: CatalogFilterSelection;
  onChange: (next: CatalogFilterSelection) => void;
}) {
  const { t, locale } = useI18n();
  const [facets, setFacets] = useState<CatalogFacets | null>(null);
  const genreKey = selection.genreIds.join(',');
  const providerKey = selection.providerIds.join(',');

  useEffect(() => {
    if (!client) return;
    let cancelled = false;
    client.catalogFacets(kind, selection)
      .then((next) => { if (!cancelled) setFacets(next); })
      .catch(() => { /* No filter offered while the server cannot answer; the catalog itself still shows. */ });
    return () => { cancelled = true; };
    // The selection's ids decide; the object identity does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, kind, genreKey, providerKey, locale]);

  if (!facets || (!facets.genres.length && !facets.providers.length)) return null;
  const active = catalogFilterActive(selection);
  return (
    <div className="catalog-filters" role="group" aria-label={t('filterCatalog')}>
      {facets.providers.length > 0 && (
        <div className="catalog-filter-row">
          <span className="catalog-filter-label">{t('filterServices')}</span>
          <div className="catalog-filter-chips">
            {facets.providers.map((provider) => (
              <button
                key={provider.id}
                type="button"
                className={`catalog-chip provider-chip ${providerChipSelected(selection.providerIds, provider) ? 'selected' : ''}`}
                aria-pressed={providerChipSelected(selection.providerIds, provider)}
                onClick={() => onChange({ ...selection, providerIds: toggleProviderChip(selection.providerIds, provider) })}
              >
                {provider.logo && <img src={provider.logo} alt="" loading="lazy" />}
                <span>{provider.name}</span>
                <small>{provider.count}</small>
              </button>
            ))}
          </div>
        </div>
      )}
      {facets.genres.length > 0 && (
        <div className="catalog-filter-row">
          <span className="catalog-filter-label">{t('genres')}</span>
          <div className="catalog-filter-chips">
            {facets.genres.map((genre) => (
              <button
                key={genre.id}
                type="button"
                className={`catalog-chip ${selection.genreIds.includes(genre.id) ? 'selected' : ''}`}
                aria-pressed={selection.genreIds.includes(genre.id)}
                onClick={() => onChange({ ...selection, genreIds: toggle(selection.genreIds, genre.id) })}
              >
                <span>{genre.name}</span>
                <small>{genre.count}</small>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="catalog-filter-footer">
        {active && (
          <button type="button" className="catalog-filter-clear" onClick={() => onChange(EMPTY_CATALOG_FILTER)}>
            {t('clearFilters')} · {facets.total}
          </button>
        )}
        {facets.providers.length > 0 && <span className="catalog-filter-source">{t('streamingDataSource', { source: facets.providersSource })}</span>}
      </div>
    </div>
  );
}
